import { InfoOutlined } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Alert, Box, Link, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useDebouncedSave } from "../../../../../hooks/useDebouncedSave";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { useJobs } from "../../../../../hooks/useJobs";
import { hrisMappingCertSortingKey } from "../../../../../services/LocalStorageService";
import type { CertificationJobMapping, CertPrimaryJobChangePreview, Job, Position } from "../../../../../utils/models";
import { NurseRole } from "../../../../../utils/models";
import { useDerivePosition } from "./derivedPosition";
import {
	buildJobIds,
	useCertPrimaryJobChangePreviewFetcher,
	useDeleteCertMapping,
	useDepartmentCertificationMapping,
	useHrisRawEmployees,
	useUpsertCertMapping,
} from "./hrisMappingHooks";
import { JobMappingChangeConfirmDialog } from "./JobMappingChangeConfirmDialog";
import { type AddCustomFieldSpec, AddCustomMappingButton, HoverActionCell, normalizeRoleString } from "./utils";

/** Flat row type for the cert grid (DataGrid needs flat fields for editing). */
interface CertMappingRow {
	roleString: string;
	/** Editable proxy for jobs[0] — the singleSelect column edits the job the role string syncs as. */
	jobId: string;
	/** Not shown or edited — carried so a save can re-send the row's existing position (DAT-804). */
	position: Position | null;
	role: string;
	/** The mapping's full ordered job list; jobs[1:] display-only here (edited in the Pretty Mapping view) and ride along on every upsert (DAT-466). */
	jobs: Job[];
	isOverride: boolean;
	isCustom: boolean;
	defaultDisplay?: string | null;
}

function toCertRows(data: CertificationJobMapping[]): CertMappingRow[] {
	return data.map((m) => ({
		roleString: m.roleString,
		jobId: m.jobs[0]?.id ?? "",
		position: m.position ?? null,
		role: m.role ?? "",
		jobs: m.jobs,
		isOverride: m.isCustom ? false : m.isOverride,
		isCustom: m.isCustom,
		defaultDisplay: m.defaultDisplay,
	}));
}

interface RolesMappingGridProps {
	/** Optional handler to switch the parent view from Raw Table to Pretty Mapping (orphan banner CTA). */
	onSwitchToPretty?: () => void;
}

export const RolesMappingGrid: FC<RolesMappingGridProps> = ({ onSwitchToPretty }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = useDepartmentCertificationMapping(departmentId);
	const { data: rawEmployees, isLoading: isLoadingRaw } = useHrisRawEmployees(departmentId);
	const jobs = useJobs();
	const upsertMutation = useUpsertCertMapping(departmentId);
	// Gates the grid the same way the Pretty Mapping pane does: `derivePositionFor` falls through to
	// the column's server default until this global table lands, so a save made in that window writes
	// `Nursing` over whatever the defaults actually say. A REJECTED load is gated too, not just a pending
	// one — it leaves the same empty map behind a dismissible snackbar, and `handleAddCustom` derives a
	// position from `null`, so an orphan mapped in that window is written as `Nursing` for good. The
	// status comes from `useDerivePosition` and NOT a second `useCertificationDefaults()` — two observers
	// of the one cache entry turned a single failed load into two identical error snackbars.
	const { derivePositionFor, isLoading: isLoadingCertDefaults, isError: isErrorCertDefaults } = useDerivePosition();
	const deleteMutation = useDeleteCertMapping(departmentId);
	const fetchPrimaryJobChangePreview = useCertPrimaryJobChangePreviewFetcher(departmentId);
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	// Pending primary-job change awaiting confirmation: the row being edited, its new first job, and the
	// preview of active linked employees the change would (or wouldn't) re-job (DAT-535).
	const [jobChangeTarget, setJobChangeTarget] = useState<{ row: CertMappingRow; newJobId: string; preview: CertPrimaryJobChangePreview } | null>(null);

	const certSortOptions: MultiSortOption<CertMappingRow>[] = useMemo(
		() => [
			{ label: t("hris.mapping.column.roleString"), sortColumns: ["roleString"] },
			{ label: t("hris.mapping.column.jobName"), sortColumns: ["jobId"] },
			{ label: t("hris.mapping.column.role"), sortColumns: ["role"] },
			{ label: t("hris.mapping.column.isOverride"), sortColumns: ["isOverride"] },
			{ label: t("hris.mapping.column.isCustom"), sortColumns: ["isCustom"] },
		],
		[t],
	);

	const scheduleSave = useDebouncedSave<{ roleString: string; jobIds: string[]; position: string; role: string | null }>((payload) => {
		upsertMutation.mutate(payload, {
			onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.roleString);
					return next;
				}),
		});
	});

	const rawRoleSet = useMemo(() => {
		// Set of normalized role_string values present in raw HRIS employees for this department.
		// Normalization mirrors the backend's `_normalize_role_string` so we join the same way the DB does.
		const s = new Set<string>();
		for (const e of rawEmployees ?? []) s.add(normalizeRoleString(e.role));
		return s;
	}, [rawEmployees]);

	const orphanCount = useMemo(() => {
		// Orphans: raw role strings that have NO cert mapping at all (default, HRIS, or override).
		// Surfaced as a banner above the Raw Table view linking to the Pretty Mapping view.
		if (!data) return 0;
		const mappingKeys = new Set(data.map((m) => normalizeRoleString(m.roleString)));
		let count = 0;
		for (const role of rawRoleSet) if (!mappingKeys.has(role)) count++;
		return count;
	}, [data, rawRoleSet]);

	const rows = useMemo(() => {
		// Custom rows are ALWAYS visible. Other rows must appear in raw employees for this department.
		const byRaw = (data ?? []).filter((m) => m.isCustom || rawRoleSet.has(normalizeRoleString(m.roleString)));
		return toCertRows(byRaw);
	}, [data, rawRoleSet]);

	const jobOptions = useMemo(() => (jobs ?? []).map((j: Job) => ({ value: j.id, label: j.displayName ?? j.name })), [jobs]);

	const handleDelete = useCallback(
		(roleString: string) => {
			deleteMutation.mutate(roleString, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
		},
		[deleteMutation, showErrorSnackbar, t],
	);

	const certAddFields: AddCustomFieldSpec[] = useMemo(
		() => [
			{ name: "jobId", label: t("hris.mapping.column.jobName"), options: (jobs ?? []).map((j) => ({ value: j.id, label: j.displayName ?? j.name })) },
			{ name: "role", label: t("hris.mapping.column.role"), options: Object.values(NurseRole).map((v) => ({ value: v, label: v })) },
		],
		[jobs, t],
	);

	const handleAddCustom = useCallback(
		(key: string, extras: Record<string, string>) => {
			upsertMutation.mutate(
				{ roleString: key, jobIds: [extras.jobId], position: derivePositionFor(key, null), role: extras.role },
				{ onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err) },
			);
		},
		[upsertMutation, showErrorSnackbar, derivePositionFor, t],
	);

	const saveCertRow = useCallback(
		(row: CertMappingRow, firstJobId: string) => {
			setPendingKeys((prev) => new Set(prev).add(row.roleString));
			scheduleSave(row.roleString, {
				roleString: row.roleString,
				// Full ordered list: the edited job first, the row's other jobs preserved after it
				// (a job promoted to the first slot can't also stay in the tail — DAT-466).
				jobIds: buildJobIds(firstJobId, row.jobs),
				position: derivePositionFor(row.roleString, row.position),
				role: row.role,
			});
		},
		[scheduleSave, derivePositionFor],
	);

	const clearPendingKey = useCallback((roleString: string) => {
		setPendingKeys((prev) => {
			const next = new Set(prev);
			next.delete(roleString);
			return next;
		});
	}, []);

	// Fetch the preview of active linked employees, then either save straight through (nobody to re-job)
	// or open the confirm dialog (DAT-535). The row shows the pending spinner while the preview is in
	// flight so the cell doesn't silently snap back to the old job with no feedback. On preview failure
	// we surface the error and leave the row reverted rather than saving an unconfirmed change.
	const openJobChangeConfirm = useCallback(
		async (row: CertMappingRow, newJobId: string) => {
			setPendingKeys((prev) => new Set(prev).add(row.roleString));
			try {
				const preview = await fetchPrimaryJobChangePreview(row.roleString, newJobId);
				if (preview.rows.length === 0) {
					// saveCertRow re-asserts the pending key and owns its lifecycle through onSettled.
					saveCertRow(row, newJobId);
					return;
				}
				clearPendingKey(row.roleString);
				setJobChangeTarget({ row, newJobId, preview });
			} catch (err) {
				clearPendingKey(row.roleString);
				showErrorSnackbar(t("hris.mapping.jobChangePreview.error"), {}, err);
			}
		},
		[fetchPrimaryJobChangePreview, saveCertRow, clearPendingKey, showErrorSnackbar, t],
	);

	const handleConfirmJobChange = useCallback(() => {
		if (!jobChangeTarget) return;
		saveCertRow(jobChangeTarget.row, jobChangeTarget.newJobId);
		setJobChangeTarget(null);
	}, [jobChangeTarget, saveCertRow]);

	const processRowUpdate = useCallback(
		(newRow: CertMappingRow, oldRow: CertMappingRow): CertMappingRow => {
			// Primary-job change on a mapping that already had a primary: confirm the propagation to linked
			// employees before saving (DAT-535). Hold the visible value at the old job until confirmed so a
			// cancel reverts cleanly; a first-time primary (no prior job) keeps the direct-save path.
			if (newRow.jobId !== oldRow.jobId && oldRow.jobId && newRow.jobId) {
				void openJobChangeConfirm(oldRow, newRow.jobId);
				return oldRow;
			}
			if (newRow.jobId !== oldRow.jobId || newRow.role !== oldRow.role) {
				saveCertRow(newRow, newRow.jobId);
			}
			return newRow;
		},
		[openJobChangeConfirm, saveCertRow],
	);

	const columns: GridColDef<CertMappingRow>[] = useMemo(
		() => [
			{
				field: "roleString",
				headerName: t("hris.mapping.column.roleString"),
				flex: 1,
				minWidth: 180,
			},
			{
				field: "jobId",
				headerName: t("hris.mapping.column.jobName"),
				flex: 1,
				minWidth: 160,
				editable: true,
				type: "singleSelect",
				valueOptions: jobOptions,
			},
			{
				field: "role",
				headerName: t("hris.mapping.column.role"),
				flex: 1,
				minWidth: 140,
				editable: true,
				type: "singleSelect",
				valueOptions: Object.values(NurseRole),
			},
			{
				// Read-only: the mapping's additional jobs (jobs[1:]) are edited in the Pretty Mapping view (DAT-466).
				field: "jobs",
				headerName: t("hris.mapping.column.additionalJobs"),
				flex: 1,
				minWidth: 160,
				sortable: false,
				valueGetter: (_value, row) =>
					row.jobs
						.slice(1)
						.map((j) => j.displayName ?? j.name)
						.join(", "),
			},
			{
				field: "isOverride",
				headerName: t("hris.mapping.column.isOverride"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) =>
					pendingKeys.has(params.row.roleString) ? (
						<JumpingDots />
					) : (
						<HoverActionCell
							show={params.row.isOverride && !params.row.isCustom}
							actionIcon={<RestoreIcon fontSize="small" />}
							tooltip={params.row.defaultDisplay ? `${t("hris.mapping.resetToDefault")}: ${params.row.defaultDisplay}` : t("hris.mapping.resetToDefault")}
							onAction={() => handleDelete(params.row.roleString)}
						/>
					),
			},
			{
				field: "isCustom",
				headerName: t("hris.mapping.column.isCustom"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) => (
					<HoverActionCell
						show={params.row.isCustom}
						actionIcon={<DeleteOutlineIcon fontSize="small" color="error" />}
						tooltip={t("hris.mapping.deleteCustom")}
						onAction={() => handleDelete(params.row.roleString)}
					/>
				),
			},
		],
		[t, jobOptions, handleDelete, pendingKeys],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%", minHeight: 0 }}>
			{orphanCount > 0 && (
				<Alert severity="info" data-testid="hris-mapping-roles-orphan-banner" sx={{ alignItems: "center" }}>
					{t("hris.mapping.orphans.banner", { count: orphanCount })}{" "}
					{onSwitchToPretty && (
						<Link
							component="button"
							type="button"
							onClick={onSwitchToPretty}
							data-testid="hris-mapping-roles-orphan-banner-switch"
							sx={{ verticalAlign: "baseline" }}
						>
							{t("hris.mapping.orphans.banner.cta")}
						</Link>
					)}
				</Alert>
			)}
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<SystemDataGrid<CertMappingRow>
					rows={rows}
					columns={columns}
					getRowId={(row) => row.roleString}
					searchableColumns={["roleString"]}
					searchPlaceholder={t("hris.mapping.search.placeholder.roleString")}
					sortingOptions={certSortOptions}
					defaultSortState={[{ sortColumns: ["roleString"], sortOrder: "asc" }]}
					localStorageKey={hrisMappingCertSortingKey}
					processRowUpdate={processRowUpdate}
					loading={isLoading || isLoadingRaw || isLoadingCertDefaults}
					error={isError || isErrorCertDefaults}
					emptyState={<EmptyState icon={<TableRowsOutlined />} title={t("hris.mapping.grid.empty.title")} hint={t("hris.mapping.grid.empty.hint")} />}
					leadingToolbarNodes={<AddCustomMappingButton onAdd={handleAddCustom} keyLabel={t("hris.mapping.column.roleString")} extraFields={certAddFields} />}
					trailingToolbarNodes={
						<Tooltip title={t("hris.mapping.addCustom.tooltip")}>
							<InfoOutlined />
						</Tooltip>
					}
					dataTestId="hris-mapping-roles"
				/>
			</Box>
			{jobChangeTarget && (
				<JobMappingChangeConfirmDialog
					key={jobChangeTarget.row.roleString}
					open
					roleString={jobChangeTarget.row.roleString}
					currentJobLabel={jobChangeTarget.row.jobs[0]?.displayName ?? jobChangeTarget.row.jobs[0]?.name ?? t("hris.mapping.jobChangeConfirm.noJob")}
					newJobLabel={jobChangeTarget.preview.newJob.displayName ?? jobChangeTarget.preview.newJob.name}
					rows={jobChangeTarget.preview.rows}
					onConfirm={handleConfirmJobChange}
					onClose={() => setJobChangeTarget(null)}
				/>
			)}
		</Box>
	);
};
